import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { fabric as FabricNS } from 'fabric';
import fabric from '@/lib/fabric';
import { usePdfStore } from '@/stores/pdfStore';
import {
  canvasToAnnotationJson,
  loadAnnotationJson,
} from '@/lib/pdf/pdfAnnotations';
import {
  computeFitScale,
  getPageSize,
  renderPageNumberToCanvas,
} from '@/lib/pdf/pdfDocument';
import { addSignatureToFabric } from '@/lib/pdf/placeSignature';
import {
  isCoarsePointer,
  overlayTopContext,
  patchFabricOverlayClear,
  stylePdfAnnotationControls,
  suppressChildSelectionChrome,
} from '@/lib/pdf/pdfFabricOverlay';

function pickImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

export default function PdfViewport() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const fabricElRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<FabricNS.Canvas | null>(null);
  const dragRef = useRef<{
    mode: 'shape' | 'highlight' | null;
    startX: number;
    startY: number;
    obj: FabricNS.Object | null;
  }>({ mode: null, startX: 0, startY: 0, obj: null });
  const skipCommitRef = useRef(false);
  /** Monotonic overlay-load generation — stale async loads must not touch the canvas/store. */
  const overlayLoadGenRef = useRef(0);
  const deleteRafRef = useRef<number | null>(null);
  const [cssSize, setCssSize] = useState({ w: 0, h: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  /** Floating delete control anchored to selection top-right (page coords) */
  const [deletePos, setDeletePos] = useState<{ left: number; top: number } | null>(null);
  const panDrag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const updateDeletePos = () => {
    if (deleteRafRef.current != null) return;
    deleteRafRef.current = window.requestAnimationFrame(() => {
      deleteRafRef.current = null;
      const canvas = fabricRef.current;
      const obj = canvas?.getActiveObject();
      if (!canvas || !obj) {
        setDeletePos(null);
        return;
      }
      // Bounding box in canvas/page coordinates
      const bound = obj.getBoundingRect(true);
      setDeletePos({
        left: bound.left + bound.width,
        top: bound.top,
      });
    });
  };

  const pdfProxy = usePdfStore((s) => s.pdfProxy);
  const activePageIndex = usePdfStore((s) => s.activePageIndex);
  const zoomMode = usePdfStore((s) => s.zoomMode);
  const tool = usePdfStore((s) => s.tool);
  const color = usePdfStore((s) => s.color);
  const highlightColor = usePdfStore((s) => s.highlightColor);
  const brushSize = usePdfStore((s) => s.brushSize);
  const fontSize = usePdfStore((s) => s.fontSize);
  const shapeKind = usePdfStore((s) => s.shapeKind);
  const selectedSignatureId = usePdfStore((s) => s.selectedSignatureId);
  const commitPageAnnotation = usePdfStore((s) => s.commitPageAnnotation);
  const setZoomMode = usePdfStore((s) => s.setZoomMode);
  const setError = usePdfStore((s) => s.setError);
  const pageCount = usePdfStore((s) => s.pageCount);
  const overlayEpoch = usePdfStore((s) => s.overlayEpoch);
  const loadedPageRef = useRef(activePageIndex);

  const commitFromCanvas = (
    pushHistory = true,
    pageIndex = loadedPageRef.current
  ) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    // Never commit while an overlay reload has cleared the canvas — force commits
    // during skipCommit were wiping all prior annotations down to an empty JSON,
    // so Finalize only kept later strokes ("last one").
    if (skipCommitRef.current) return;
    const liveCount = canvas.getObjects().length;
    const existing = usePdfStore.getState().getPageAnnotation(pageIndex);
    const existingCount = Array.isArray(existing?.objects)
      ? (existing.objects as unknown[]).length
      : 0;
    // Silent flushes must not overwrite a richer store with an empty live canvas.
    // Explicit edits (pushHistory) may delete everything.
    if (!pushHistory && liveCount === 0 && existingCount > 0) return;
    const json = canvasToAnnotationJson(canvas);
    commitPageAnnotation(pageIndex, json, pushHistory);
  };

  const handleDeleteSelected = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const selected = canvas.getActiveObjects();
    if (!selected.length) return;
    selected.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setDeletePos(null);
    commitFromCanvas(true);
  };

  // Init / dispose Fabric once + register export flush
  useEffect(() => {
    const el = fabricElRef.current;
    if (!el) return;
    const coarse = isCoarsePointer();
    const canvas = new fabric.Canvas(el, {
      selection: true,
      preserveObjectStacking: true,
      stopContextMenu: true,
      // Retina + incomplete upper-canvas clears = trailing selection handles on iOS
      enableRetinaScaling: !coarse,
      allowTouchScrolling: false,
      targetFindTolerance: coarse ? 12 : 4,
    });
    patchFabricOverlayClear(canvas);
    fabricRef.current = canvas;

    const onObjectAdded = (e: { target?: FabricNS.Object }) => {
      if (e.target) stylePdfAnnotationControls(e.target, coarse);
    };
    const onBeforeRender = () => {
      const top = overlayTopContext(canvas);
      if (top) canvas.clearContext(top);
    };
    canvas.on('object:added', onObjectAdded);
    canvas.on('before:render', onBeforeRender);

    const onChanged = () => {
      commitFromCanvas(true);
      updateDeletePos();
    };
    const onSelection = () => {
      suppressChildSelectionChrome(canvas.getActiveObject());
      updateDeletePos();
    };
    const onCleared = () => setDeletePos(null);

    canvas.on('object:modified', onChanged);
    // Do NOT commit on object:removed — canvas.clear() / reload fires removals and
    // can race-commit an empty overlay (leaving only later annotations for Finalize).
    // Delete + eraser already call commitFromCanvas explicitly.
    canvas.on('path:created', onChanged);
    canvas.on('selection:created', onSelection);
    canvas.on('selection:updated', onSelection);
    canvas.on('selection:cleared', onCleared);
    canvas.on('object:moving', onSelection);
    canvas.on('object:scaling', onSelection);
    canvas.on('object:rotating', onSelection);
    canvas.on('mouse:up', onSelection);

    usePdfStore.getState().setFlushActiveOverlay(() => {
      const fc = fabricRef.current;
      if (!fc) return;
      if (usePdfStore.getState().suspendOverlayCommits) return;
      // Mid-reload canvas is empty/partial — keep store/drafts as source of truth
      if (skipCommitRef.current) return;
      const liveCount = fc.getObjects().length;
      const pageIndex = loadedPageRef.current;
      const existing = usePdfStore.getState().getPageAnnotation(pageIndex);
      const existingCount = Array.isArray(existing?.objects)
        ? (existing.objects as unknown[]).length
        : 0;
      if (liveCount === 0 && existingCount > 0) return;
      const json = canvasToAnnotationJson(fc);
      usePdfStore.getState().commitPageAnnotation(pageIndex, json, false);
    });
    usePdfStore.getState().setClearActiveOverlay(() => {
      const fc = fabricRef.current;
      if (!fc) return;
      // Bump gen so any in-flight overlay load abandons after this clear
      overlayLoadGenRef.current += 1;
      skipCommitRef.current = true;
      try {
        fc.discardActiveObject();
        fc.clear();
        fc.requestRenderAll();
      } finally {
        skipCommitRef.current = false;
      }
      setDeletePos(null);
    });

    return () => {
      canvas.off('object:added', onObjectAdded);
      canvas.off('before:render', onBeforeRender);
      canvas.off('object:modified', onChanged);
      canvas.off('path:created', onChanged);
      canvas.off('selection:created', onSelection);
      canvas.off('selection:updated', onSelection);
      canvas.off('selection:cleared', onCleared);
      canvas.off('object:moving', onSelection);
      canvas.off('object:scaling', onSelection);
      canvas.off('object:rotating', onSelection);
      canvas.off('mouse:up', onSelection);
      usePdfStore.getState().setFlushActiveOverlay(null);
      usePdfStore.getState().setClearActiveOverlay(null);
      if (deleteRafRef.current != null) {
        window.cancelAnimationFrame(deleteRafRef.current);
        deleteRafRef.current = null;
      }
      canvas.dispose();
      fabricRef.current = null;
      setDeletePos(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Keep Fabric pointer mapping correct when the stage scrolls (mobile).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onScrollOrResize = () => fabricRef.current?.calcOffset();
    wrap.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('orientationchange', onScrollOrResize);
    return () => {
      wrap.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('orientationchange', onScrollOrResize);
    };
  }, []);

  // Render PDF page + sync Fabric size / annotations (not on every annotation commit)
  useEffect(() => {
    const proxy = pdfProxy;
    const pdfCanvas = pdfCanvasRef.current;
    const wrap = wrapRef.current;
    const canvas = fabricRef.current;
    if (!proxy || !pdfCanvas || !wrap || !canvas) return;

    // Always flush the page we are leaving before any reload clears the canvas.
    // Skip during Finalize so baked pages are not re-layered with the old overlay.
    if (
      loadedPageRef.current !== activePageIndex &&
      !usePdfStore.getState().suspendOverlayCommits &&
      !skipCommitRef.current
    ) {
      commitFromCanvas(false, loadedPageRef.current);
    }

    let cancelled = false;
    const pageIndex = activePageIndex;

    const applyObjectInteractivity = () => {
      const currentTool = usePdfStore.getState().tool;
      canvas.getObjects().forEach((obj) => {
        obj.selectable = currentTool !== 'hand' && currentTool !== 'brush';
        obj.evented = currentTool !== 'hand' && currentTool !== 'brush';
        obj.lockMovementX = false;
        obj.lockMovementY = false;
        obj.setCoords();
      });
    };

    const loadOverlayForSize = async (width: number, height: number) => {
      const gen = ++overlayLoadGenRef.current;
      // Snapshot annotation JSON before clearing — never re-read after a concurrent wipe
      const json = usePdfStore.getState().getPageAnnotation(pageIndex);
      const hasObjects =
        json && Array.isArray(json.objects) && json.objects.length > 0;
      const jsonSnapshot = hasObjects
        ? (JSON.parse(JSON.stringify(json)) as ReturnType<
            typeof canvasToAnnotationJson
          >)
        : null;

      skipCommitRef.current = true;
      try {
        if (cancelled || gen !== overlayLoadGenRef.current) return;

        canvas.setWidth(width);
        canvas.setHeight(height);
        canvas.setZoom(1);
        canvas.calcOffset();

        await loadAnnotationJson(canvas, jsonSnapshot);
        // Stale load must not keep applying transform / page index
        if (cancelled || gen !== overlayLoadGenRef.current) return;

        if (jsonSnapshot) {
          const storedW = Number(jsonSnapshot.width) || width;
          const storedH = Number(jsonSnapshot.height) || height;
          if (storedW !== width || storedH !== height) {
            const sx = width / storedW;
            const sy = height / storedH;
            canvas.getObjects().forEach((obj) => {
              obj.scaleX = (obj.scaleX || 1) * sx;
              obj.scaleY = (obj.scaleY || 1) * sy;
              obj.left = (obj.left || 0) * sx;
              obj.top = (obj.top || 0) * sy;
              obj.setCoords();
            });
          }
        }
        applyObjectInteractivity();
        canvas.getObjects().forEach((obj) => stylePdfAnnotationControls(obj));
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        loadedPageRef.current = pageIndex;
        setDeletePos(null);
      } finally {
        // Only the latest load may clear skipCommit / resume commits
        if (gen === overlayLoadGenRef.current) {
          skipCommitRef.current = false;
          // After Finalize, applyFinalizedPdf leaves suspend=true until the baked
          // PDF finishes loading — resume only once that load wins.
          if (usePdfStore.getState().suspendOverlayCommits) {
            usePdfStore.getState().resumeOverlayCommits();
          }
        }
      }
    };

    const run = async () => {
      try {
        const size = await getPageSize(proxy, pageIndex + 1);
        if (cancelled) return;

        const rect = wrap.getBoundingClientRect();
        const scale =
          zoomMode === 'auto'
            ? computeFitScale(size.widthPts, size.heightPts, rect.width, rect.height)
            : zoomMode;

        const { width, height } = await renderPageNumberToCanvas(
          proxy,
          pageIndex + 1,
          pdfCanvas,
          scale
        );
        if (cancelled) return;

        setCssSize({ w: width, h: height });
        await loadOverlayForSize(width, height);
      } catch (err) {
        skipCommitRef.current = false;
        console.warn('PDF page render failed', err);
      }
    };

    void run();

    let resizeTimer: number | null = null;
    const ro = new ResizeObserver(() => {
      if (zoomMode !== 'auto') return;
      // Debounce — on resize, flush live objects then re-fit (do not drop other pages)
      if (resizeTimer != null) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (cancelled) return;
        if (loadedPageRef.current === pageIndex && !skipCommitRef.current) {
          commitFromCanvas(false, pageIndex);
        }
        // Invalidate in-flight overlay loads before starting another
        overlayLoadGenRef.current += 1;
        void run();
      }, 180);
    });
    ro.observe(wrap);

    return () => {
      cancelled = true;
      overlayLoadGenRef.current += 1;
      // Flush this page before effect tears down (page switch / unmount).
      // Never flush during Finalize — that re-introduced duplicates on the baked PDF.
      // Never flush while skipCommit (canvas may be empty mid-reload).
      if (
        loadedPageRef.current === pageIndex &&
        !usePdfStore.getState().suspendOverlayCommits &&
        !skipCommitRef.current
      ) {
        commitFromCanvas(false, pageIndex);
      }
      if (resizeTimer != null) window.clearTimeout(resizeTimer);
      ro.disconnect();
    };
    // commitFromCanvas is stable enough via refs/store; omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfProxy, activePageIndex, zoomMode]);

  // Reload overlay after undo/redo / Finalize without always re-rendering PDF
  useEffect(() => {
    if (overlayEpoch === 0) return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    const gen = ++overlayLoadGenRef.current;
    // After Finalize, store is empty — clear editable overlay (ink is in the PDF now)
    const json = usePdfStore.getState().suspendOverlayCommits
      ? null
      : usePdfStore.getState().getPageAnnotation(activePageIndex);
    const hasObjects =
      json && Array.isArray(json.objects) && json.objects.length > 0;
    const snapshot = hasObjects
      ? (JSON.parse(JSON.stringify(json)) as NonNullable<typeof json>)
      : null;
    skipCommitRef.current = true;
    void loadAnnotationJson(canvas, snapshot)
      .then(() => {
        if (gen !== overlayLoadGenRef.current) return;
        canvas.getObjects().forEach((obj) => stylePdfAnnotationControls(obj));
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        setDeletePos(null);
      })
      .catch((err) => console.warn('Overlay reload failed', err))
      .finally(() => {
        if (gen === overlayLoadGenRef.current) {
          skipCommitRef.current = false;
          if (usePdfStore.getState().suspendOverlayCommits) {
            usePdfStore.getState().resumeOverlayCommits();
          }
        }
      });
  }, [overlayEpoch, activePageIndex]);

  // Tool mode configuration
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const placeTools =
      tool === 'sign' ||
      tool === 'shapes' ||
      tool === 'text' ||
      tool === 'image' ||
      tool === 'highlight';
    // Pointer + placement tools can select/move; brush/hand cannot
    const canSelect = tool === 'pointer' || tool === 'eraser' || placeTools;

    canvas.isDrawingMode = false;
    canvas.selection = tool === 'pointer';
    canvas.defaultCursor =
      tool === 'hand' ? 'grab' : tool === 'sign' || tool === 'shapes' || tool === 'highlight'
        ? 'crosshair'
        : 'default';
    canvas.hoverCursor = tool === 'hand' ? 'grab' : 'move';
    canvas.forEachObject((obj) => {
      obj.selectable = canSelect;
      obj.evented = tool !== 'hand' && tool !== 'brush';
      obj.lockMovementX = false;
      obj.lockMovementY = false;
      obj.hasControls = canSelect;
      obj.hasBorders = canSelect;
    });
    suppressChildSelectionChrome(canvas.getActiveObject());

    if (tool === 'brush') {
      canvas.isDrawingMode = true;
      const brush = canvas.freeDrawingBrush;
      if (brush) {
        brush.color = color;
        brush.width = brushSize;
      }
    }

    canvas.requestRenderAll();
  }, [tool, color, brushSize]);

  // Interaction handlers
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const selectExisting = (target: FabricNS.Object) => {
      canvas.setActiveObject(target);
      canvas.requestRenderAll();
    };

    const onDown = (opt: { e: Event; target?: FabricNS.Object }) => {
      const pointer = canvas.getPointer(opt.e);
      const target = opt.target;

      if (tool === 'eraser' && target) {
        canvas.remove(target);
        commitFromCanvas(true);
        return;
      }

      // Clicking an existing annotation selects/moves it (do not insert another)
      if (target && tool !== 'brush' && tool !== 'hand' && tool !== 'eraser') {
        selectExisting(target);
        if (tool === 'text' && target instanceof fabric.IText) {
          target.enterEditing();
        }
        return;
      }

      if (tool === 'text') {
        const text = new fabric.IText('Text', {
          left: pointer.x,
          top: pointer.y,
          fill: color,
          fontSize,
          fontFamily: 'Source Sans 3, sans-serif',
          name: 'pdf-text',
          selectable: true,
          evented: true,
        });
        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        commitFromCanvas(true);
        return;
      }

      if (tool === 'sign') {
        const sig = usePdfStore.getState().getSelectedSignature();
        if (!sig?.dataUrl && !sig?.downloadUrl) {
          setError('Create or select a signature first');
          return;
        }
        void (async () => {
          try {
            if (!fabricRef.current) return;
            // Don't activate on touch — the placing tap would start a transform
            // and leave trailing selection handles on iOS.
            await addSignatureToFabric({
              canvas,
              signature: sig!,
              left: pointer.x,
              top: pointer.y,
              activate: !isCoarsePointer(),
            });
            commitFromCanvas(true);
            // Defer tool switch so the current gesture isn't cancelled mid-place
            window.setTimeout(() => {
              usePdfStore.getState().setTool('pointer');
            }, 0);
          } catch (err) {
            console.error(err);
            setError(
              err instanceof Error
                ? err.message
                : 'Could not place signature — try creating it again'
            );
          }
        })();
        return;
      }

      if (tool === 'image') {
        void (async () => {
          const file = await pickImageFile();
          if (!file) return;
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              if (typeof result === 'string') resolve(result);
              else reject(new Error('Failed to read image'));
            };
            reader.onerror = () => reject(new Error('Failed to read image'));
            reader.readAsDataURL(file);
          });
          fabric.Image.fromURL(dataUrl, (img) => {
            const maxW = canvas.getWidth() * 0.45;
            const scale = Math.min(1, maxW / (img.width || maxW));
            img.set({
              left: pointer.x,
              top: pointer.y,
              scaleX: scale,
              scaleY: scale,
              name: 'pdf-image',
              selectable: true,
              evented: true,
            });
            canvas.add(img);
            canvas.setActiveObject(img);
            commitFromCanvas(true);
          });
        })();
        return;
      }

      if (tool === 'shapes' || tool === 'highlight') {
        dragRef.current = {
          mode: tool === 'highlight' ? 'highlight' : 'shape',
          startX: pointer.x,
          startY: pointer.y,
          obj: null,
        };
      }
    };

    const onMove = (opt: { e: Event }) => {
      const drag = dragRef.current;
      if (!drag.mode) return;
      const pointer = canvas.getPointer(opt.e);
      const left = Math.min(drag.startX, pointer.x);
      const top = Math.min(drag.startY, pointer.y);
      const width = Math.abs(pointer.x - drag.startX);
      const height = Math.abs(pointer.y - drag.startY);

      if (!drag.obj) {
        if (drag.mode === 'highlight') {
          drag.obj = new fabric.Rect({
            left,
            top,
            width: Math.max(width, 1),
            height: Math.max(height, brushSize),
            fill: highlightColor,
            opacity: 0.4,
            strokeWidth: 0,
            name: 'pdf-highlight',
            selectable: true,
            evented: true,
          });
        } else if (shapeKind === 'ellipse') {
          drag.obj = new fabric.Ellipse({
            left,
            top,
            rx: Math.max(width / 2, 1),
            ry: Math.max(height / 2, 1),
            fill: 'transparent',
            stroke: color,
            strokeWidth: Math.max(1, brushSize / 2),
            name: 'pdf-shape',
            selectable: true,
            evented: true,
          });
        } else {
          drag.obj = new fabric.Rect({
            left,
            top,
            width: Math.max(width, 1),
            height: Math.max(height, 1),
            fill: 'transparent',
            stroke: color,
            strokeWidth: Math.max(1, brushSize / 2),
            name: 'pdf-shape',
            selectable: true,
            evented: true,
          });
        }
        canvas.add(drag.obj);
      } else if (drag.obj instanceof fabric.Ellipse) {
        drag.obj.set({
          left,
          top,
          rx: Math.max(width / 2, 1),
          ry: Math.max(height / 2, 1),
        });
      } else {
        drag.obj.set({
          left,
          top,
          width: Math.max(width, 1),
          height:
            drag.mode === 'highlight' ? Math.max(height, brushSize) : Math.max(height, 1),
        });
      }
      canvas.requestRenderAll();
    };

    const onUp = () => {
      if (dragRef.current.mode && dragRef.current.obj) {
        commitFromCanvas(true);
      }
      dragRef.current = { mode: null, startX: 0, startY: 0, obj: null };
    };

    canvas.on('mouse:down', onDown);
    canvas.on('mouse:move', onMove);
    canvas.on('mouse:up', onUp);

    return () => {
      canvas.off('mouse:down', onDown);
      canvas.off('mouse:move', onMove);
      canvas.off('mouse:up', onUp);
    };
  }, [
    tool,
    color,
    highlightColor,
    brushSize,
    fontSize,
    shapeKind,
    selectedSignatureId,
    activePageIndex,
    setError,
  ]);

  // Hand pan on wrapper
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (tool !== 'hand') return;
    panDrag.current = { x: e.clientX, y: e.clientY, ox: pan.x, oy: pan.y };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!panDrag.current || tool !== 'hand') return;
    setPan({
      x: panDrag.current.ox + (e.clientX - panDrag.current.x),
      y: panDrag.current.oy + (e.clientY - panDrag.current.y),
    });
  };
  const onPointerUp = () => {
    panDrag.current = null;
  };

  return (
    <div className="pdfe-viewport">
      <div
        ref={wrapRef}
        className={`pdfe-viewport-stage${tool === 'hand' ? ' is-panning' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <div
          className="pdfe-page-stack"
          style={{
            width: cssSize.w || undefined,
            height: cssSize.h || undefined,
            // left/top — not transform. A CSS transform on this ancestor
            // makes iOS leave Fabric selection chrome on the upper canvas.
            left: pan.x || undefined,
            top: pan.y || undefined,
          }}
        >
          <canvas ref={pdfCanvasRef} className="pdfe-pdf-canvas" />
          <div
            className="pdfe-fabric-host"
            style={{ pointerEvents: tool === 'hand' ? 'none' : 'auto' }}
          >
            <canvas ref={fabricElRef} />
          </div>
          {deletePos && tool !== 'hand' && (
            <button
              type="button"
              className="pdfe-sel-delete"
              style={{
                left: deletePos.left,
                top: deletePos.top,
              }}
              title="Delete"
              aria-label="Delete selected object"
              onPointerDown={(e) => {
                // Don't start a fabric drag / page pan
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSelected();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 12h12a1 1 0 0 0 1-1V8H5v12a1 1 0 0 0 1 1z"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="pdfe-viewport-bar">
        <label className="pdfe-zoom">
          <span className="sr-only">Zoom</span>
          <select
            value={zoomMode === 'auto' ? 'auto' : String(zoomMode)}
            onChange={(e) => {
              const v = e.target.value;
              setZoomMode(v === 'auto' ? 'auto' : Number(v));
              setPan({ x: 0, y: 0 });
            }}
          >
            <option value="auto">Auto Size</option>
            <option value="0.5">50%</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
            <option value="2">200%</option>
          </select>
        </label>
        <span className="pdfe-page-indicator">
          {pageCount ? `${activePageIndex + 1} of ${pageCount}` : '—'}
        </span>
      </div>
    </div>
  );
}
